import { useState, useContext } from 'react'
import { useParams } from 'react-router'
import { ADD_AGENT_PROSPECT, AGENTS_BY_CLIENT } from '../../graphql'
import { StoreContext } from '../../store'
import { withClient } from '../../hocs'
import { useMutation, useQuery } from '@apollo/client'
import * as reducers from '../../store/reducers'
import CloseIcon from '@mui/icons-material/Close'
import MobilePhoneIcon from '@mui/icons-material/StayCurrentPortrait'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import PersonIcon from '@mui/icons-material/Person'
import PhoneMaskedInput from '../PhoneMaskedInput/PhoneMaskedInput'
import { getImagePrefix } from '@anewgo/utils'
import { isEmail, isPhone } from '../../utils'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Input from '@mui/material/Input'
import InputLabel from '@mui/material/InputLabel'
import Button from '@mui/material/Button'
import Avatar from '@mui/material/Avatar'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemText from '@mui/material/ListItemText'
import FormHelperText from '@mui/material/FormHelperText'
import FormControl from '@mui/material/FormControl'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import FaceIcon from '@mui/icons-material/Face'
import {
  EmptyAgentList,
  AgentList,
  CloseButton,
  ListContainer,
} from './AgentLoginDialogStyles'

const AgentLoginDialog = ({ isOpen, setIsOpen, client }) => {
  const { dispatch, prospect } = useContext(StoreContext)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')

  const [nameError, setNameError] = useState(false)
  const [phoneError, setPhoneError] = useState(false)
  const [emailError, setEmailError] = useState(false)

  const [isNewAgentSelection, setIsNewAgentSelection] = useState(false)
  const [selectedAgent, setSelectedAgent] = useState(null)

  const [agentFilter, setAgentFilter] = useState(/.*/)

  const { clientName } = useParams()
  const [addAgentProspect] = useMutation(ADD_AGENT_PROSPECT)
  const {
    loading,
    error,
    data: agentsByClientData,
  } = useQuery(AGENTS_BY_CLIENT, {
    variables: {
      clientName: clientName,
    },
  })

  if (loading) return null
  if (error) return `Error! ${error}`
  const availableAgents = agentsByClientData.agentsByClient

  const handleClose = () => {
    setIsOpen(false)
  }

  const handleListChange = (agent) => {
    setSelectedAgent(agent)
  }

  const handleNameChange = (nameInput) => {
    setName(nameInput)
  }

  const handleNameBlur = () => {
    setName((prevName) => prevName.trim())
    setNameError(!name.trim().length)
  }

  const handleEmailsChange = (emailsInput) => {
    setEmail(emailsInput)
  }

  const handleEmailOnBlur = () => {
    setEmailError(!isEmail(email))
  }

  const handlePhoneChange = (phoneInput) => {
    setPhone(phoneInput)
    setPhoneError(false)
  }

  const handlePhoneBlur = () => {
    setPhoneError(!isPhone(phone))
  }

  const isFormValid = () =>
    name.trim().length && isEmail(email) && isPhone(phone)

  const validateForm = () => {
    let valid = true

    if (!name.trim().length) {
      setNameError(true)
      valid = false
    }

    if (!isEmail(email)) {
      setEmailError(true)
      valid = false
    }

    if (!isPhone(phone)) {
      setPhoneError(true)
      valid = false
    }

    return valid
  }

  const handleSignIn = (e) => {
    e.preventDefault()

    if (isNewAgentSelection) {
      if (!validateForm()) return
    }

    const localAgent = {
      name: isNewAgentSelection
        ? name
        : `${selectedAgent.firstName} ${selectedAgent.lastName}`,
      email: isNewAgentSelection ? email : selectedAgent.email,
      phone: isNewAgentSelection ? phone : selectedAgent.phone,
    }

    if (prospect && isNewAgentSelection) {
      addAgentProspect({
        variables: {
          clientName,
          prospectEmail: prospect.email,
          agent: {
            name: localAgent.name,
            phone: localAgent.phone,
            email: localAgent.email,
          },
          source: 'truss',
        },
      })
    }

    dispatch(reducers.setLocalAgent(localAgent))

    handleClose()
  }

  const renderAgentsList = () => {
    const filteredAgents = availableAgents.filter(({ firstName, lastName }) =>
      `${firstName} ${lastName}`.toLowerCase().match(agentFilter)
    )

    return (
      <>
        <ListContainer>
          <TextField
            id="agent-search-input"
            label="Filter Agents"
            type="text"
            variant="standard"
            valute={agentFilter}
            onChange={(e) =>
              setAgentFilter(new RegExp(`.*${e.target.value.toLowerCase()}.*`))
            }
            style={{ flexGrow: 1 }}
          />
        </ListContainer>
        {filteredAgents.length > 0 ? (
          <AgentList dense>
            {filteredAgents.map((agent) => (
              <ListItem
                key={agent.id}
                button
                selected={selectedAgent === agent}
                onClick={() => handleListChange(agent)}
              >
                <ListItemAvatar>
                  <Avatar
                    alt={`Agent ${agent.lastName} profile picture`}
                    src={
                      agent.picture &&
                      `${getImagePrefix(
                        client.directoryName,
                        'images/agents/'
                      )}${agent.picture}`
                    }
                  >
                    {agent.firstName.charAt(0) + agent.lastName.charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={`${agent.firstName} ${agent.lastName}`}
                />
              </ListItem>
            ))}
          </AgentList>
        ) : (
          <EmptyAgentList dense>
            <FaceIcon fontSize="large" />
            No agents found
          </EmptyAgentList>
        )}
      </>
    )
  }

  const renderNewAgentForm = () => (
    <DialogContent>
      <FormControl required margin="normal" variant="standard" fullWidth={true}>
        <InputLabel error={nameError} htmlFor="user-name-input">
          Name
        </InputLabel>
        <Input
          onChange={(ev) => handleNameChange(ev.target.value)}
          onBlur={handleNameBlur}
          id="user-name-input"
          fullWidth
          autoFocus
          value={name}
          required
          error={nameError}
          startAdornment={
            <InputAdornment position={'start'}>
              <PersonIcon />
            </InputAdornment>
          }
        />
        {nameError && (
          <FormHelperText error={nameError} id="user-name-help">
            Enter a valid name
          </FormHelperText>
        )}
        {!nameError && <FormHelperText required>Required</FormHelperText>}
      </FormControl>
      <FormControl required margin="normal" variant="standard" fullWidth={true}>
        <InputLabel error={emailError} htmlFor="user-email-input">
          Email Address
        </InputLabel>
        <Input
          id="user-email-input"
          fullWidth
          required
          error={emailError}
          value={email}
          onChange={(ev) => handleEmailsChange(ev.target.value)}
          onBlur={handleEmailOnBlur}
          startAdornment={
            <InputAdornment position={'start'}>
              <MailOutlineIcon />
            </InputAdornment>
          }
        />
        {emailError && (
          <FormHelperText error={emailError} id="user-email-help">
            Enter a valid e-mail
          </FormHelperText>
        )}
        {!emailError && <FormHelperText required>Required</FormHelperText>}
      </FormControl>
      <FormControl required margin="normal" variant="standard" fullWidth={true}>
        <InputLabel error={phoneError} htmlFor="user-phone-input">
          Phone number
        </InputLabel>
        <Input
          id="user-phone-input"
          aria-describedby="user-phone-help"
          error={phoneError}
          fullWidth
          placeholder={'Phone number'}
          value={phone}
          onChange={(ev) => handlePhoneChange(ev.target.value)}
          onBlur={handlePhoneBlur}
          inputComponent={PhoneMaskedInput}
          startAdornment={
            <InputAdornment position={'start'}>
              <MobilePhoneIcon />
            </InputAdornment>
          }
        />
        {phoneError && (
          <FormHelperText error={phoneError} id="user-phone-help">
            Enter a valid phone number
          </FormHelperText>
        )}
        {!phoneError && <FormHelperText required>Required</FormHelperText>}
      </FormControl>
    </DialogContent>
  )

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
      maxWidth="xs"
      fullWidth={true}
    >
      <CloseButton
        color="inherit"
        onClick={handleClose}
        aria-label="Close"
        size="large"
      >
        <CloseIcon />
      </CloseButton>
      <DialogTitle id="form-dialog-title">Agent Login</DialogTitle>
      {isNewAgentSelection ? renderNewAgentForm() : renderAgentsList()}
      <DialogActions>
        <Button
          onClick={() => setIsNewAgentSelection((state) => !state)}
          color="primary"
        >
          {isNewAgentSelection ? 'Choose from list' : 'New Agent'}
        </Button>
        <Button
          type="submit"
          color="primary"
          variant="contained"
          onClick={handleSignIn}
          disabled={isNewAgentSelection ? !isFormValid() : !selectedAgent}
        >
          Sign-in
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default withClient(AgentLoginDialog)
